import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  Badge,
  Button,
  Divider,
  Tab,
  Tabs,
  Avatar,
  Snackbar,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as EmergencyIcon,
  WhatsApp as WhatsAppIcon,
  Sms as SmsIcon,
  Notifications as NotificationIcon,
  Psychology as AIIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
  Timer as TimerIcon
} from '@mui/icons-material';

interface Notification {
  id: string;
  type: 'emergency' | 'symptom_alert' | 'medication_reminder' | 'appointment' | 'general';
  title: string;
  message: string;
  timestamp: string;
  channel: 'whatsapp' | 'sms' | 'push' | 'call';
  status: 'sent' | 'delivered' | 'read';
  priority: 'high' | 'medium' | 'low';
  patientName?: string;
  triggeredBy?: string;
}

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
  userRole?: string;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ 
  open, 
  onClose,
  userRole = 'family_caregiver'
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  const [notifications] = useState<Notification[]>([
    {
      id: '1',
      type: 'emergency',
      title: 'ALERTA DE EMERGENCIA',
      message: 'Juan Pérez ha activado el botón de emergencia',
      timestamp: 'Hace 2 minutos',
      channel: 'whatsapp',
      status: 'delivered',
      priority: 'high',
      patientName: 'Juan Pérez',
      triggeredBy: 'patient'
    },
    {
      id: '2',
      type: 'symptom_alert',
      title: 'Evaluación de Síntomas Urgente',
      message: 'María González (Cuidadora) reportó síntomas críticos de Juan Pérez: Dolor en el pecho, Dificultad para respirar',
      timestamp: 'Hace 15 minutos',
      channel: 'sms',
      status: 'read',
      priority: 'high',
      patientName: 'Juan Pérez',
      triggeredBy: 'María González'
    },
    {
      id: '3',
      type: 'medication_reminder',
      title: 'Recordatorio de Medicamento',
      message: 'Juan Pérez debe tomar Enalapril 10mg',
      timestamp: 'Hace 1 hora',
      channel: 'push',
      status: 'read',
      priority: 'medium',
      patientName: 'Juan Pérez'
    },
    {
      id: '4',
      type: 'appointment',
      title: 'Cita Mañana',
      message: 'Recordatorio: Juan Pérez tiene cita con Dr. Silva mañana a las 10:00',
      timestamp: 'Hace 3 horas',
      channel: 'whatsapp',
      status: 'delivered',
      priority: 'low',
      patientName: 'Juan Pérez'
    }
  ]);

  const emergencyNotifications = notifications.filter(n => n.type === 'emergency');
  const alertNotifications = notifications.filter(n => n.type === 'symptom_alert');
  const allNotifications = notifications;

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'whatsapp': return <WhatsAppIcon color="success" />;
      case 'sms': return <SmsIcon color="primary" />;
      case 'push': return <NotificationIcon />;
      case 'call': return <PhoneIcon color="error" />;
      default: return <NotificationIcon />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'emergency': return <EmergencyIcon color="error" />;
      case 'symptom_alert': return <AIIcon color="warning" />;
      case 'medication_reminder': return <TimerIcon color="info" />;
      case 'appointment': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const simulateNewNotification = () => {
    setSnackbarMessage('Nueva notificación de emergencia recibida');
    setShowSnackbar(true);
  };

  const getNotificationsByTab = () => {
    switch (activeTab) {
      case 0: return allNotifications;
      case 1: return emergencyNotifications;
      case 2: return alertNotifications;
      default: return allNotifications;
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Centro de Notificaciones
            </Typography>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label={`Todas (${allNotifications.length})`} />
            <Tab 
              label={
                <Badge badgeContent={emergencyNotifications.length} color="error">
                  Emergencias
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={alertNotifications.length} color="warning">
                  Alertas IA
                </Badge>
              } 
            />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 2 }}>
          {/* Demo Alert */}
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            action={
              <Button size="small" onClick={simulateNewNotification}>
                Simular Nueva
              </Button>
            }
          >
            Modo Demo: Las notificaciones mostradas son ejemplos del sistema
          </Alert>

          <List>
            {getNotificationsByTab().map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem 
                  sx={{ 
                    bgcolor: notification.status === 'sent' ? 'action.hover' : 'transparent',
                    borderRadius: 1,
                    mb: 1
                  }}
                >
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: `${getPriorityColor(notification.priority)}.light` }}>
                      {getTypeIcon(notification.type)}
                    </Avatar>
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {notification.title}
                        </Typography>
                        <Chip 
                          label={notification.priority === 'high' ? 'URGENTE' : 
                                 notification.priority === 'medium' ? 'IMPORTANTE' : 'INFO'} 
                          size="small"
                          color={getPriorityColor(notification.priority) as any}
                        />
                        {notification.triggeredBy && (
                          <Chip 
                            label={`Por: ${notification.triggeredBy}`} 
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {notification.message}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getChannelIcon(notification.channel)}
                            <Typography variant="caption">
                              {notification.channel === 'whatsapp' ? 'WhatsApp' :
                               notification.channel === 'sms' ? 'SMS' :
                               notification.channel === 'push' ? 'App' : 'Llamada'}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {notification.timestamp}
                          </Typography>
                          {notification.status === 'delivered' && (
                            <Chip 
                              icon={<CheckIcon />} 
                              label="Entregado" 
                              size="small" 
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    {notification.type === 'emergency' && (
                      <Button 
                        variant="contained" 
                        color="error" 
                        size="small"
                        onClick={() => {}}
                      >
                        Responder
                      </Button>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
                {index < getNotificationsByTab().length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          {/* Notification Flow Explanation */}
          <Paper sx={{ p: 2, mt: 3, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Flujo de Notificaciones:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><WhatsAppIcon color="success" /></ListItemIcon>
                <ListItemText 
                  primary="WhatsApp (Principal)"
                  secondary="Mensajes instantáneos para emergencias y alertas importantes"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><SmsIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="SMS (Respaldo)"
                  secondary="Se envía si WhatsApp falla o no hay confirmación en 2 minutos"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><PhoneIcon color="error" /></ListItemIcon>
                <ListItemText 
                  primary="Llamada Automática"
                  secondary="Para emergencias sin respuesta después de 5 minutos"
                />
              </ListItem>
            </List>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* Snackbar for new notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        message={snackbarMessage}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={() => setShowSnackbar(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </>
  );
};

export default NotificationCenter;